import { useDispatch, useSelector } from "react-redux";
import {
  Component,
  newComponent,
  UseComponentSearchProps,
} from "../types/component";
import { newComponentBlock } from "../types/notebook/note/block/component";
import { useInsertBlock } from "./page/block";
import { useSelectedNotebookPageIdMap } from "./page";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { AddComponentsAction } from "../reducer/component";
import { writeData } from "../api";
import { insertPageBlock as insertPageBlockApi } from "../api/page";
import { useContext, useEffect, useMemo, useState } from "react";
import { makeSelectComponent, selectComponents } from "../selectors/ui";
import {
  getAllComponents,
  getComponent,
  searchComponents,
} from "../api/component";
import { useAppIdMap } from "./app";
import { debounce } from "lodash";

export const useAddComponents = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (comps: Component[]) => {
    const action: AddComponentsAction = {
      type: ActionType.ADD_COMPONENTS,
      components: comps,
    };
    dispatch(action as any);
  };
};

export const useInsertComponentBlock = () => {
  const { selectedNotebookPage } = useSelectedNotebookPageIdMap();
  const insertBlock = useInsertBlock();
  const addComponents = useAddComponents();

  return (config: { blockIndex: number }) => {
    const { blockIndex } = config;
    const comp = newComponent({
      name: "Component",
      page: selectedNotebookPage!,
    });
    const block = newComponentBlock({ comp, blockIndex });
    addComponents([comp]);
    insertBlock({ block, blockIndex });
    insertPageBlockApi({ block, "block-index": blockIndex });
    writeData(comp).then((r) => {
      // console.log("AND_WE_WROTE_THEM", r);
    });
    // // console.log("INSERTING_COMPONNNET", blockIndex, comp, block);
    // Promise.all([writeData(comp), writeData(block)]).then((r) => {});
  };
};

export const useComponent = (compId: string | undefined) => {
  const selectComponent = useMemo(() => makeSelectComponent(compId), [compId]);
  const component = useSelector(selectComponent);
  const writeBackend = useMemo(
    () =>
      debounce((component: Component) => {
        writeData(component);
      }, 500),
    [compId],
  );

  const addComponents = useAddComponents();
  const setComponent = (comp: Component) => {
    addComponents([comp]);
    writeBackend(comp);
  };
  const setComponentName = (name: string) => {
    if (!component) return;
    const newComp: Component = { ...component, name };
    setComponent(newComp);
  };
  return { component, setComponent, setComponentName };
};

export const useFetchComponent = () => {
  const addComponents = useAddComponents();
  const { appId } = useAppIdMap();

  return (compId: string) => {
    if (compId === "omega/query-omega/component/page/PageComponent") {
      return Promise.resolve(undefined);
    }
    return getComponent({ "component-id": compId, "app-id": appId! }).then(
      (comp) => {
        addComponents([comp]);
        return comp;
      },
    );
  };
};

export const useFetchComponentListener = (compId: string | undefined) => {
  const fetchComponent = useFetchComponent();
  useEffect(() => {
    if (!compId) return;
    fetchComponent(compId);
  }, [compId]);
};

export const useFetchAllComponents = () => {
  const addComponents = useAddComponents();
  const { appId } = useAppIdMap();

  return () => {
    return getAllComponents({ "app-id": appId! }).then((comps) => {
      addComponents(comps);
      return comps;
    });
  };
};

export const useFetchAllComponentsListener = () => {
  const fetchAllComponents = useFetchAllComponents();

  useEffect(() => {
    fetchAllComponents();
  }, []);
};

export const useComponents = () => {
  const compMap = useSelector(selectComponents);
  const components = Object.values(compMap);
  return { components };
};

export const useComponentSearch = (
  config: { disabledSearch?: boolean } = { disabledSearch: false },
) => {
  const { disabledSearch } = config;
  const [searchString, setSearchString] = useState<string>("");
  const { appId } = useAppIdMap();
  const [page, setPage] = useState<Component[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const addComponents = useAddComponents();

  const handleSearch = useMemo(
    () =>
      debounce((searchString: string) => {
        setIsLoading(true);
        searchComponents({ "app-id": appId!, search: searchString }).then(
          (comps) => {
            setPage(comps);
            addComponents(comps);
            setIsLoading(false);
          },
        );
      }, 200),
    [appId],
  );

  useEffect(() => {
    if (!disabledSearch) {
      handleSearch(searchString);
    }
  }, [searchString, disabledSearch]);

  const useComponentProps: UseComponentSearchProps = {
    searchString,
    setSearchString,
    page,
    isLoading,
    setIsLoading,
  };

  return useComponentProps;
};
